/**
 * 外觀（日間／夜晚／跟系統）＋ 字級（設計 v2 S7；UI v2 **步 9**）
 *
 *   · 外觀：寫 `<html data-theme="light|dark">`（`tokens.css` 有 `[data-theme="dark"]` 區塊）
 *   · 字級：用中央庫 **`@coeldery/font-scale`**（5 段，寫 `html[data-font-scale]`＋inline px）
 *   · 兩者都記住（`localStorage`）
 */
import { useEffect, useState } from 'react';
import { useFontScale, type FontScaleLevel } from '@coeldery/font-scale';

export type ThemeChoice = 'light' | 'dark' | 'system';

const THEME_KEY = 'cosingery.theme';
export const FONT_KEY = 'cosingery.fontScale';

/** 設計 §1：5 段（14／16／18／20／23px；key 對應 `html[data-font-scale="1…5"]`） */
export const FONT_LEVELS: readonly FontScaleLevel[] = [
  { key: '1', rootPx: 14 },
  { key: '2', rootPx: 16 },
  { key: '3', rootPx: 18 },
  { key: '4', rootPx: 20 },
  { key: '5', rootPx: 23 },
];

function applyTheme(theme: ThemeChoice): void {
  if (typeof document === 'undefined') return;
  const sysDark =
    typeof window !== 'undefined' && typeof window.matchMedia === 'function'
      ? window.matchMedia('(prefers-color-scheme: dark)').matches
      : false;
  const dark = theme === 'dark' || (theme === 'system' && sysDark);
  document.documentElement.setAttribute('data-theme', dark ? 'dark' : 'light');
}

export function useAppearance() {
  const [theme, setTheme] = useState<ThemeChoice>(() => {
    try {
      const v = localStorage.getItem(THEME_KEY);
      return v === 'dark' || v === 'light' || v === 'system' ? v : 'system';
    } catch {
      return 'system';
    }
  });

  useEffect(() => {
    try {
      localStorage.setItem(THEME_KEY, theme);
    } catch {
      /* 私隱模式 */
    }
    applyTheme(theme);
  }, [theme]);

  /** 跟系統：系統轉深淺色即刻跟 */
  useEffect(() => {
    if (theme !== 'system' || typeof window.matchMedia !== 'function') return;
    const mq = window.matchMedia('(prefers-color-scheme: dark)');
    const onChange = () => applyTheme('system');
    mq.addEventListener('change', onChange);
    return () => mq.removeEventListener('change', onChange);
  }, [theme]);

  const { level, levels, setLevel } = useFontScale({ levels: FONT_LEVELS, defaultKey: '2', storageKey: FONT_KEY });

  return { theme, setTheme, fontLevel: level, fontLevels: levels, setFontLevel: setLevel };
}
